import React, { useEffect, useRef, useState } from 'react';
import Stepper from '@/components/common/Stepper';
import InputField from '@/components/common/InputField';
import { RightArrow } from '@/assets/RightArrow';
import { LeftArrow } from '@/assets/LeftArrow';
import {
  MultiStepFormProps,
  ChangeStepFormEventValue,
  ChangeStepFormEvent,
} from './multi-step-form.model';
import {
  fieldsWithDynamicOtherInput,
  emailRegex,
  invalidEmailDomains,
  BUDGET_VALUE_MAPPER,
} from './multi-step-form.utils';
import HubSpotEmbedForm, {
  HubspotFormRef,
} from '@/components/form/HubSpotEmbedForm';

const portalId = `${process.env.NEXT_PUBLIC_HS_PLATFORM_ID}`;

const MultiStepForm = ({
  stepsConfig,
  stepLabels,
  formType,
  onStepChange,
  onNameChange,
}: MultiStepFormProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(
    stepsConfig.reduce(
      (acc, step) => {
        step.fields.forEach((field) => (acc[field.name] = ''));
        return acc;
      },
      {} as Record<string, ChangeStepFormEventValue>,
    ),
  );

  const [dynamicFieldState, setDynamicFieldState] = useState<
    Record<string, { displayTextInput?: boolean; textInputValue?: string }>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const hubspotFormRef1 = useRef<HubspotFormRef>(null);
  const hubspotFormRef2 = useRef<HubspotFormRef>(null);
  const hubspotFormId1 = formType.STEP_1;
  const hubspotFormId2 = formType.STEP_2;

  useEffect(() => {
    onStepChange(currentStep);
  }, [currentStep, onStepChange]);

  useEffect(() => {
    onNameChange(
      `${formData['firstname'] || ''} ${formData['lastname'] || ''}`,
    );
  }, [formData, onNameChange]);

  const isStepValid = () => {
    return stepsConfig[currentStep]?.fields.every((field) => {
      const fieldValue = formData[field.name];
      if (field.required) {
        if (
          fieldsWithDynamicOtherInput.includes(field.name) &&
          (dynamicFieldState[field.name]?.displayTextInput ??
            field.displayTextInput)
        ) {
          return !!(
            dynamicFieldState[field.name]?.textInputValue ??
            field.textInputValue
          );
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.length > 0;
        }
        if (field.type === 'email') {
          return validateWorkEmail(fieldValue as string) && !!fieldValue;
        }
        return !!fieldValue;
      }
      if (field.type === 'email') {
        return validateWorkEmail(fieldValue as string) && !!fieldValue;
      }
      return true;
    });
  };

  const handleInputChange = (e: ChangeStepFormEvent) => {
    const { name, value, textInputValue } = e.target;
    if (submitError) {
      setSubmitError(false);
    }

    if (fieldsWithDynamicOtherInput.includes(name)) {
      let isOtherSelected = false;
      if (Array.isArray(value)) {
        isOtherSelected = (value as string[])
          .map((s) => s.toLowerCase())
          .includes('other');
      } else if (typeof value === 'string') {
        isOtherSelected = value.toLowerCase() === 'other';
      }
      setDynamicFieldState((prev) => ({
        ...prev,
        [name]: { displayTextInput: isOtherSelected, textInputValue },
      }));
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateWorkEmail = (email: string) => {
    return (
      emailRegex.test(email) &&
      !invalidEmailDomains.includes(email.trim().split('@')?.[1])
    );
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const isLastFormStep = (): boolean => {
    return currentStep === stepsConfig.length - 1;
  };

  const isFirstStep = (): boolean => {
    return currentStep === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(false);
    setIsSubmitting(true);

    try {
      if (isFirstStep() && isStepValid()) {
        const sent = await sendFormData(formData, formType.STEP_1);
        if (sent) {
          setCurrentStep((prev) => prev + 1);
        } else {
          setSubmitError(true);
        }
      } else if (isLastFormStep() && isStepValid()) {
        const sent = await sendFormData(formData, formType.STEP_2);
        if (sent) {
          setCurrentStep((prev) => prev + 1);
        } else {
          setSubmitError(true);
        }
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  async function sendFormData(
    formData: Record<string, ChangeStepFormEventValue>,
    formId: string,
  ): Promise<boolean> {
    const fieldsToReset: string[] = [];
    const formattedFields = Object.entries(formData)
      .filter((field) => !!field[1])
      .map(([name, value]) => {
        if (name === 'additional_files' && value instanceof File) {
          return {
            name,
            value,
          };
        }
        let formattedValue = value;
        if (name === 'budget') {
          formattedValue =
            BUDGET_VALUE_MAPPER[value as keyof typeof BUDGET_VALUE_MAPPER];
        }
        if (isLastFormStep() && fieldsWithDynamicOtherInput.includes(name)) {
          const editedFieldIndex = stepsConfig[currentStep]?.fields.findIndex(
            (field) => field.name === name,
          );
          const editedField =
            stepsConfig[currentStep]?.fields[editedFieldIndex];
          if (editedField) {
            const textInputValue =
              dynamicFieldState[name]?.textInputValue ??
              editedField.textInputValue;
            formattedValue = `${formattedValue}${
              textInputValue ? ';' + textInputValue : ''
            }`;
            fieldsToReset.push(name);
          }
        }
        return {
          name: `${name}`,
          value: `${formattedValue}`,
        };
      });

    if (fieldsToReset.length > 0) {
      setDynamicFieldState((prev) => {
        const next = { ...prev };
        fieldsToReset.forEach((name) => {
          next[name] = { displayTextInput: false, textInputValue: '' };
        });
        return next;
      });
    }

    switch (formId) {
      case formType.STEP_1:
        return (
          hubspotFormRef1.current?.submitHubspotForm(formattedFields) ?? false
        );
      case formType.STEP_2:
        return (
          hubspotFormRef2.current?.submitHubspotForm(formattedFields) ?? false
        );
      default:
        return false;
    }
  }

  return (
    <>
      <form
        className="max-w-4xl md:p-6 rounded-lg multi-form flex-grow"
        onSubmit={handleSubmit}
      >
        <Stepper
          currentStep={currentStep}
          totalSteps={stepsConfig.length}
          stepLabels={stepLabels}
        />
        <React.Fragment>
          {stepsConfig[currentStep]?.eyebrow && (
            <h3 className="mt-20 text-sm font-semibold uppercase tracking-wide text-primary-green">
              {stepsConfig[currentStep].eyebrow}
            </h3>
          )}
          <h2
            className={`font-bold text-4xl mb-7 ${
              stepsConfig[currentStep]?.eyebrow ? 'mt-2' : 'mt-7'
            }`}
          >
            {stepsConfig[currentStep]?.title}
          </h2>

          <p className="font-normal text-xl mb-11">
            {stepsConfig[currentStep]?.content}
          </p>

          {stepsConfig[currentStep]?.fields.map((field) => (
            <InputField
              key={field.name}
              label={field.label}
              name={field.name}
              type={field.type}
              required={field.required}
              value={formData[field.name]}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
              options={field.options}
              columns={field.columns}
              textInputPlaceHolder={field.textInputPlaceHolder}
              textInputValue={
                dynamicFieldState[field.name]?.textInputValue ??
                field.textInputValue
              }
              displayTextInput={
                dynamicFieldState[field.name]?.displayTextInput ??
                field.displayTextInput
              }
              hasError={submitError}
            />
          ))}
        </React.Fragment>

        {!!(formData['email'] as string)?.length &&
          !validateWorkEmail(formData['email'] as string) && (
            <p className="text-center text-red-600 font-semibold">
              Email field must be a valid work email
            </p>
          )}
        <div className="flex justify-start my-8 flex-wrap gap-2">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="px-4 py-3 text-primary-green rounded-full font-medium flex justify-between items-center m-auto md:m-0"
            >
              <span className="mr-2">
                <LeftArrow stroke="#069183" />
              </span>
              Go back
            </button>
          )}

          <button
            type="submit"
            disabled={!isStepValid() || isSubmitting}
            className={`px-4 py-3 text-white rounded-full font-semibold flex justify-between items-center m-auto md:m-0
              ${
                !isStepValid() || isSubmitting
                  ? 'cursor-not-allowed bg-[#cccccc]'
                  : 'bg-gradient-to-r from-primary-green to-primary-green-shade'
              }`}
          >
            {isSubmitting ? (
              'Submitting...'
            ) : isLastFormStep() ? (
              'Submit'
            ) : (
              <>
                Continue
                <span className="ml-2">
                  <RightArrow />
                </span>
              </>
            )}
          </button>
        </div>
        {submitError && (
          <p className="text-center text-red-600 font-semibold mt-2">
            Something went wrong. Please try again.
          </p>
        )}
      </form>
      <HubSpotEmbedForm
        ref={hubspotFormRef1}
        portalId={portalId}
        formId={hubspotFormId1}
      />
      <HubSpotEmbedForm
        ref={hubspotFormRef2}
        portalId={portalId}
        formId={hubspotFormId2}
      />
    </>
  );
};

export default MultiStepForm;
